import orderBy from 'lodash/orderBy'
import uniqBy from 'lodash/uniqBy'

export function isObject(x) {
    return x !== null && typeof x === 'object' && x === Object(x) && !isArray(x)
}

export function isArray(x) {
    return Array.isArray(x)
}

export function isNumber(x) {
    return typeof x === 'number'
}

export function isEmptyObject(x) {
    return isObject(x) && Object.keys(x).length === 0
}

export function arrayize(values) {
    // return empty array if values is empty
    if (typeof values === 'undefined' || values === null)
        return []

    // return array of values, converting to array if necessary
    return isArray(values) ? values : [ values ]
}

export function getDescendantProp(obj, desc) {
    const arr = desc.split('.')
    let child = obj
    while (arr.length && child) {
        child = child[arr.shift()]
    }

    return child
}

export function fieldSorter(fields) {
    return (a, b) => fields.map(o => {
        let dir = 1
        let p = o
        if (o[0] === '-') {
            dir = -1
            p = o.substring(1)
        }
        return a[p] > b[p] ? dir : a[p] < b[p] ? -(dir) : 0
    }).reduce((p, n) => p || n, 0)
}

export function pluck(arrayOfObjects, property) {
    return arrayOfObjects.map(obj => obj[property])
}

export function copy(obj) {
    return JSON.parse(JSON.stringify(obj))
}

export const curry = (fn, ...args1) => {
    if (typeof fn !== 'function')
        throw new Error('fn param needs to be a function')

    return (...args2) => fn(...args1, ...args2)
}

export const capitalize = str => str[0].toUpperCase() + str.slice(1)

// export function debounce(func, wait) {
//     let timeout
//     return function (...args) {
//         const context = this
//         clearTimeout(timeout)
//         timeout = setTimeout(() => func.apply(context, args), wait)
//     }
// }

// Returns a function, that, as long as it continues to be invoked, will not
// be triggered. The function will be called after it stops being called for
// N milliseconds. If `immediate` is passed, trigger the function on the
// leading edge, instead of the trailing.
// https://davidwalsh.name/javascript-debounce-function
export function debounce(func, wait, immediate) {
    let timeout
    return function () {
        const context = this
        const args = arguments
        const later = () => {
            timeout = null
            if (!immediate) func.apply(context, args)
        }
        const callNow = immediate && !timeout
        clearTimeout(timeout)
        timeout = setTimeout(later, wait)
        if (callNow) func.apply(context, args)
    }
}

export const now = () => Date.now ? Date.now : () => new Date().getTime

export function throttle(func, wait, options) {
    let context, args, result
    let timeout = null
    let previous = 0
    options || (options = {})
    const later = function () {
        previous = options.leading === false ? 0 : now()
        timeout = null
        result = func.apply(context, args)
        context = args = null
    }
    return function () {
        const _now = now()
        if (!previous && options.leading === false) previous = _now
        const remaining = wait - (_now - previous)
        context = this
        args = arguments
        if (remaining <= 0) {
            clearTimeout(timeout)
            timeout = null
            previous = _now
            result = func.apply(context, args)
            context = args = null
        } else if (!timeout && options.trailing !== false)
            timeout = setTimeout(later, remaining)

        return result
    }
}

import Base64 from 'Base64'

export function getQRvalue(id, prefix) {
    return `${prefix.toLowerCase()}${id}-${Base64.btoa(id).replace(/=+/g, '')}`
}

export function booleanify(val) {
    if (val === 'true' || val === 1)
        return true

    if (val === 'false' || val === 0 || !val)
        return false

    return val
}

export const stripEmptyKeys = obj => {
    const newObj = {}
    Object.keys(obj).forEach(key => {
        if (typeof obj[key] !== 'undefined' && !!obj[key])
            newObj[key] = obj[key]

    })
    return newObj
}

export function detectIE() {
    const ua = window.navigator.userAgent

    // Test values; Uncomment to check result …

    // IE 10
    // ua = 'Mozilla/5.0 (compatible; MSIE 10.0; Windows NT 6.2; Trident/6.0)';

    // IE 11
    // ua = 'Mozilla/5.0 (Windows NT 6.3; Trident/7.0; rv:11.0) like Gecko';

    // Edge 12 (Spartan)
    // ua = 'Mozilla/5.0 (Windows NT 10.0; WOW64) AppleWebKit/537.36 (KHTML, like Gecko)
    // Chrome/39.0.2171.71 Safari/537.36 Edge/12.0';

    // Edge 13
    // ua = 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko)
    // Chrome/46.0.2486.0 Safari/537.36 Edge/13.10586';

    const msie = ua.indexOf('MSIE ')
    // IE 10 or older => return version number
    if (msie > 0)
        return parseInt(ua.substring(msie + 5, ua.indexOf('.', msie)), 10)

    const trident = ua.indexOf('Trident/')
    // IE 11 => return version number
    if (trident > 0) {
        const rv = ua.indexOf('rv:')
        return parseInt(ua.substring(rv + 3, ua.indexOf('.', rv)), 10)
    }

    const edge = ua.indexOf('Edge/')
    // Edge (IE 12+) => return version number
    if (edge > 0)
        return parseInt(ua.substring(edge + 5, ua.indexOf('.', edge)), 10)

    // other browser
    return false
}

// eslint-disable-next-line max-len
export const isMobile = (a = navigator.userAgent || navigator.vendor || window.opera) => (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4)))

export const isValidDate = d => d instanceof Date && !isNaN(d)

export const makeCancelable = promise => {
    let rejectFn

    const wrappedPromise = new Promise((resolve, reject) => {
        rejectFn = reject

        Promise.resolve(promise)
            .then(resolve)
            .catch(reject)
    })

    wrappedPromise.cancel = () => {
        rejectFn({ canceled: true })
    }

    return wrappedPromise
}

export const range = (start, stop, step = 1) => {
    if (start && !stop) {
        stop = start
        start = 0
    }
    return [ ...Array(stop - start).keys() ]
        .filter(i => !(i % Math.round(step)))
        .map(v => start + v)
}

Array.prototype.flatten = function () {
    return this.reduce((collector, value) => [ ...collector, ...value ], [])
}


Array.prototype.unique = function () {
    return Array.from(new Set(this.filter(i => i)))
}

Array.prototype.orderBy = function (...iteratees) {
    return orderBy(this, ...iteratees)
}

Array.prototype.uniqBy = function (iteratee) {
    return uniqBy(this, iteratee)
}
